import { useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataContext } from 'App';
import CartService from 'services/cart';
import { t } from 'i18n';
import { handleApiError, handleApiSuccess } from 'components/Handle';

/**
 * CRA bridge for the Next.js SEO storefront (CLAUDE.md step D20 part A):
 * /add-to-cart?product=<id>&qty=<n> adds the item (wrapped in
 * ProtectedRoute in App.js, same login-required rule as every other
 * add-to-cart path today) and redirects to /cart. Next.js product pages
 * link here instead of duplicating cart logic in two frontends.
 */
export default function AddToCart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCartList, setSnackbar } = useContext(DataContext);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const run = async () => {
      const productId = Number(searchParams.get('product'));
      const quantity = Number(searchParams.get('qty')) || 1;

      if (!Number.isFinite(productId) || productId <= 0) {
        setSnackbar({ ...handleApiError(t('productPage.invalid')), open: true });
        navigate('/cart', { replace: true });
        return;
      }

      const result = await CartService.addToCart(productId, quantity);
      if (result.success) {
        const cartResult = await CartService.getCart();
        if (cartResult.success) setCartList(cartResult.cartItems);
        setSnackbar({ ...handleApiSuccess(t('productPage.added')), open: true });
      } else {
        setSnackbar({ ...handleApiError(result.error || t('productPage.addItemFailed')), open: true });
      }
      navigate('/cart', { replace: true });
    };

    run();
  }, [navigate, searchParams, setCartList, setSnackbar]);

  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
    </div>
  );
}
